import {
  ComponentPropsWithRef,
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useMemo,
} from 'react';
import { FC } from 'react';

import { Typography } from '../typography/typography';
import { Switch } from '../switch/switch';
import { Label } from '../label/label';
import { cn } from '../../utils';

type SwitchType = ComponentPropsWithRef<typeof Switch>;

const SwitchGroupContext = createContext<{
  onChange: (value: SwitchType['value'][]) => void;
  value: SwitchType['value'][];
}>({
  value: [],
  onChange: () => {},
});

export const SwitchGroup: FC<
  PropsWithChildren<{
    onChange: (value: SwitchType['value'][]) => void;
    value: SwitchType['value'][];
    className?: string;
  }>
> = ({ value, onChange, className, children }) => {
  const [selected, setSelected] = useState(value || []);

  const handleChange = useCallback(
    (value: SwitchType['value'][]) => {
      setSelected(value);
      onChange?.(value);
    },
    [onChange],
  );

  useEffect(() => {
    if (value !== selected) {
      setSelected(value);
    }
  }, [value, selected]);

  return (
    <SwitchGroupContext.Provider value={{ value: selected, onChange: handleChange }}>
      <div className={cn('flex flex-col items-center gap-2', className)}>{children}</div>
    </SwitchGroupContext.Provider>
  );
};

export const SwitchGroupItem: FC<{ labelClassName?: string; value: string } & SwitchType> = ({
  asChild,
  value,
  id,
  name,
  children,
  labelClassName,
  ...props
}) => {
  const { value: selected, onChange } = useContext(SwitchGroupContext);

  const isSelected = useMemo(() => selected.includes(value), [selected, value]);

  const handleChange = useCallback(
    (checked: boolean) => {
      const currentValue = selected || [];
      onChange(checked ? [...currentValue, value] : currentValue.filter((v) => v !== value));
    },
    [selected, value, onChange],
  );

  return (
    <>
      <Label className={cn('flex items-center gap-2', labelClassName)} htmlFor={id}>
        {asChild ? (
          children
        ) : (
          <Typography variant="labelMediumBook" element="label">
            {children}
          </Typography>
        )}
      </Label>
      <Switch
        onCheckedChange={handleChange}
        checked={isSelected}
        value={value}
        name={name}
        id={id}
        {...props}
      />
    </>
  );
};
